import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ThumbsUp, ThumbsDown, RotateCcw, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import mythBusterBg from "@/assets/myth-busters-split.jpg";
import { supabase } from "@/integrations/supabase/client";

interface MythCard {
  id: string;
  statement: string;
  isFact: boolean;
  explanation: string;
  category: string;
}

const mythCards: MythCard[] = [
  {
    id: "myth-1",
    statement: "HRT causes breast cancer",
    isFact: false,
    explanation: "MYTH! HRT (Hormone Replacement Therapy — medicine that tops up the hormones your body is making less of) doesn't 'cause' breast cancer. The scary headlines trace back to one 2002 study (WHI) done mostly in women in their 60s using an older formulation. For most women under 60, or within 10 years of their last period, current guidelines say the benefits generally outweigh the risks. Any change in risk is small and worth weighing with your own doctor.",
    category: "HRT"
  },
  {
    id: "fact-1",
    statement: "Exercise reduces hot flash frequency and severity",
    isFact: true,
    explanation: "FACT! Regular exercise can reduce hot flash frequency by 40-50%. It also improves mood, sleep, and overall quality of life during menopause.",
    category: "Lifestyle"
  },
  {
    id: "myth-2",
    statement: "Menopause means your sex life is over",
    isFact: false,
    explanation: "MYTH! While menopause can affect sexuality, many women report satisfying sex lives after menopause. With proper treatment for vaginal symptoms and open communication, intimacy can thrive.",
    category: "Sexuality"
  },
  {
    id: "fact-2",
    statement: "Vaginal estrogen is safe even if you can't take systemic HRT",
    isFact: true,
    explanation: "FACT! Vaginal estrogen has minimal systemic absorption and is considered safe for most women, even those with contraindications to systemic HRT.",
    category: "Treatment"
  },
  {
    id: "myth-3",
    statement: "You just have to tough it out - everyone goes through this",
    isFact: false,
    explanation: "MYTH! You don't have to suffer. There are evidence-based treatments available - hormone therapy, non-hormonal medications, and lifestyle strategies that really work.",
    category: "Treatment"
  },
  {
    id: "fact-3",
    statement: "Brain fog during menopause is caused by hormonal changes, not permanent brain damage",
    isFact: true,
    explanation: "FACT! Your brain is adapting to lower estrogen, not deteriorating. Most women find cognitive function stabilizes after the transition.",
    category: "Symptoms"
  },
  {
    id: "myth-4",
    statement: "Natural remedies are safer than prescribed hormones",
    isFact: false,
    explanation: "MYTH! 'Natural' doesn't always mean safer. Many herbal supplements lack regulation and evidence. Prescribed hormones are studied, regulated, and monitored for safety.",
    category: "Treatment"
  },
  {
    id: "fact-4",
    statement: "Menopause can begin in your 40s",
    isFact: true,
    explanation: "FACT! Perimenopause (the transition) typically begins in your 40s, though it can start earlier. The average age of menopause is 51.",
    category: "Timeline"
  },
  {
    id: "myth-5",
    statement: "Weight gain during menopause is inevitable and can't be prevented",
    isFact: false,
    explanation: "MYTH! While metabolism slows and fat redistributes during menopause, weight gain isn't inevitable. Strength training, protein intake, and staying active can maintain healthy weight.",
    category: "Lifestyle"
  },
  {
    id: "fact-5",
    statement: "Sleep problems during menopause can worsen all other symptoms",
    isFact: true,
    explanation: "FACT! Poor sleep amplifies hot flashes, mood issues, brain fog, and pain. Prioritizing sleep often improves multiple symptoms at once.",
    category: "Symptoms"
  },
  {
    id: "myth-6",
    statement: "If your mother had an easy menopause, you will too",
    isFact: false,
    explanation: "MYTH! While genetics play a role in timing, symptom experience varies widely. Your mother's journey doesn't predict yours.",
    category: "General"
  },
  {
    id: "fact-6",
    statement: "Bone loss accelerates during the first 5-10 years after menopause",
    isFact: true,
    explanation: "FACT! You can lose up to 20% of bone density in this period. That's why bone health strategies are crucial during this time.",
    category: "Health"
  },
  {
    id: "myth-7",
    statement: "Menopause means you're old and past your prime",
    isFact: false,
    explanation: "MYTH! With today's life expectancy, you'll spend 30-40 years post-menopause. Many women report feeling more confident and liberated after menopause.",
    category: "General"
  },
  {
    id: "fact-7",
    statement: "Starting HRT within 10 years of menopause has the best benefit-to-risk ratio",
    isFact: true,
    explanation: "FACT! The 'timing hypothesis' shows that starting HRT early in menopause provides the most benefits with lowest risks, particularly for heart health.",
    category: "HRT"
  },
  {
    id: "myth-8",
    statement: "All women experience menopause the same way",
    isFact: false,
    explanation: "MYTH! About 25% have minimal symptoms, 50% have moderate symptoms, and 25% have severe symptoms. Experience varies dramatically.",
    category: "Symptoms"
  },
  {
    id: "fact-8",
    statement: "Anxiety and depression rates increase during perimenopause",
    isFact: true,
    explanation: "FACT! Hormonal fluctuations affect neurotransmitters. You're 2-4x more likely to experience mood disorders during perimenopause than at other life stages.",
    category: "Mental Health"
  },
  {
    id: "myth-9",
    statement: "Soy products can make menopause symptoms worse",
    isFact: false,
    explanation: "MYTH! Soy contains phytoestrogens that may actually help reduce hot flashes and support bone health. Asian women who consume soy regularly report fewer symptoms.",
    category: "Nutrition"
  },
  {
    id: "fact-9",
    statement: "Calcium alone isn't enough - you need vitamin D and K2 for bone health",
    isFact: true,
    explanation: "FACT! Vitamin D helps absorb calcium, and K2 directs it to bones instead of arteries. The trio works together for optimal bone strength.",
    category: "Nutrition"
  },
  {
    id: "myth-10",
    statement: "You can't get pregnant during perimenopause",
    isFact: false,
    explanation: "MYTH! You're still ovulating irregularly and can get pregnant. You're only officially menopausal after 12 consecutive months without a period.",
    category: "General"
  },
  {
    id: "fact-10",
    statement: "Testosterone therapy can help with energy and libido in menopause",
    isFact: true,
    explanation: "FACT! Women produce testosterone too, and levels decline with age. Adding testosterone to HRT can improve energy, mood, libido, and muscle mass.",
    category: "HRT"
  },
  {
    id: "myth-11",
    statement: "Hot flashes only last a year or two",
    isFact: false,
    explanation: "MYTH! While the average is 7-10 years, some women experience them for 10+ years. About 10% of women still have hot flashes in their 70s.",
    category: "Symptoms"
  },
  {
    id: "fact-11",
    statement: "Strength training is more important than cardio for menopause",
    isFact: true,
    explanation: "FACT! Strength training builds bone density, increases metabolism, prevents muscle loss, and improves insulin sensitivity - all crucial during menopause.",
    category: "Lifestyle"
  },
  {
    id: "myth-12",
    statement: "HRT must be stopped after 5 years",
    isFact: false,
    explanation: "MYTH! The 5-year limit came from outdated research. Current guidelines say HRT can be continued as long as benefits outweigh risks for the individual.",
    category: "HRT"
  },
  {
    id: "fact-12",
    statement: "Joint pain and stiffness are common menopause symptoms",
    isFact: true,
    explanation: "FACT! Estrogen has anti-inflammatory properties. When levels drop, many women experience joint pain, stiffness, and even frozen shoulder.",
    category: "Symptoms"
  },
  {
    id: "myth-13",
    statement: "You need to suffer through symptoms to avoid 'chemicals'",
    isFact: false,
    explanation: "MYTH! Bio-identical hormones are chemically identical to what your body makes. Suffering unnecessarily can lead to bone loss, heart issues, and diminished quality of life.",
    category: "Treatment"
  },
  {
    id: "fact-13",
    statement: "Alcohol can worsen hot flashes and disrupt sleep",
    isFact: true,
    explanation: "FACT! Alcohol triggers hot flashes, disrupts sleep architecture, and worsens mood symptoms. Reducing intake often improves multiple symptoms.",
    category: "Lifestyle"
  },
  {
    id: "myth-14",
    statement: "Hysterectomy automatically means instant menopause",
    isFact: false,
    explanation: "MYTH! Only if your ovaries are removed (oophorectomy). If ovaries remain, they continue producing hormones until natural menopause age.",
    category: "General"
  },
  {
    id: "fact-14",
    statement: "Skin changes during menopause are caused by collagen loss",
    isFact: true,
    explanation: "FACT! You lose about 30% of collagen in the first 5 years after menopause. This causes thinning, dryness, and increased wrinkles.",
    category: "Health"
  },
  {
    id: "myth-15",
    statement: "Black cohosh is proven to treat hot flashes",
    isFact: false,
    explanation: "MYTH! Despite popularity, large studies show black cohosh is no more effective than placebo. Some women report benefit, but evidence is lacking.",
    category: "Treatment"
  },
  {
    id: "myth-16",
    statement: "Underwire bras cause breast cancer",
    isFact: false,
    explanation: "MYTH! No scientific evidence supports this at all. This myth originated from concerns about restricting lymph flow, but that's not how breast cancer develops. Wear whatever bra is comfortable.",
    category: "Breast Health"
  },
  {
    id: "fact-15",
    statement: "About 85% of breast cancers occur in women with NO family history",
    isFact: true,
    explanation: "FACT! While family history increases risk, most women with breast cancer don't have this risk factor. Everyone needs to stay vigilant with screening.",
    category: "Breast Health"
  },
  {
    id: "myth-17",
    statement: "Deodorant and antiperspirants cause breast cancer",
    isFact: false,
    explanation: "MYTH! Multiple studies have found no link between these products and breast cancer. The parabens and aluminum don't cause cancer. Use your preferred deodorant without worry.",
    category: "Breast Health"
  },
  {
    id: "fact-16",
    statement: "Regular exercise reduces breast cancer risk by 10-20%",
    isFact: true,
    explanation: "FACT! Aim for 150 minutes of moderate activity per week. Physical activity is one of the most proven ways to reduce breast cancer risk.",
    category: "Breast Health"
  },
  {
    id: "myth-18",
    statement: "A mammogram can cause breast cancer to spread",
    isFact: false,
    explanation: "MYTH! Completely false. The compression during a mammogram cannot cause cancer to spread. This harmful myth stops women from getting life-saving screening.",
    category: "Breast Health"
  },
  {
    id: "fact-17",
    statement: "Breast density matters more than breast size for cancer risk",
    isFact: true,
    explanation: "FACT! Dense breasts (more glandular tissue) have higher cancer risk. Size doesn't matter - a small breast can be dense, a large breast can be mostly fatty.",
    category: "Breast Health"
  },
  {
    id: "myth-19",
    statement: "Finding a lump means you definitely have cancer",
    isFact: false,
    explanation: "MYTH! About 80% of breast lumps are NOT cancer. Many are cysts or benign tumors. Still, all new lumps should be checked by a doctor.",
    category: "Breast Health"
  },
  {
    id: "fact-18",
    statement: "Vaginal dryness and urinary symptoms worsen over time without treatment",
    isFact: true,
    explanation: "FACT! Unlike hot flashes that often improve, genitourinary symptoms typically get worse with time. But vaginal estrogen is safe and highly effective.",
    category: "Treatment"
  },
  {
    id: "myth-20",
    statement: "Frequent UTIs during menopause are just bad luck",
    isFact: false,
    explanation: "MYTH! Lower estrogen causes vaginal pH changes that increase infection risk. Vaginal estrogen can prevent recurrent UTIs remarkably well.",
    category: "Bladder Health"
  },
  {
    id: "fact-19",
    statement: "Anxiety during menopause has biological causes, not just stress",
    isFact: true,
    explanation: "FACT! Changing estrogen affects serotonin, GABA, and other brain chemicals. About 40% of women experience increased anxiety during menopause transition.",
    category: "Mental Health"
  },
  {
    id: "myth-21",
    statement: "Panic attacks and hot flashes are completely unrelated",
    isFact: false,
    explanation: "MYTH! Hot flashes can trigger panic attacks, and anxiety can trigger hot flashes. The physical sensations overlap and can create a feedback loop.",
    category: "Mental Health"
  },
  {
    id: "fact-20",
    statement: "Poor sleep amplifies all other menopause symptoms",
    isFact: true,
    explanation: "FACT! Sleep deprivation worsens hot flashes, brain fog, mood issues, and pain. Prioritizing sleep often improves multiple symptoms simultaneously.",
    category: "Sleep"
  },
  {
    id: "myth-22",
    statement: "Brain fog during menopause means permanent cognitive decline",
    isFact: false,
    explanation: "MYTH! Your brain is adapting to lower estrogen, not deteriorating. Most women find cognitive function stabilizes after the transition.",
    category: "Symptoms"
  },
  {
    id: "fact-21",
    statement: "Bladder pain syndrome (IC) can feel like a constant UTI but without infection",
    isFact: true,
    explanation: "FACT! IC causes chronic bladder and pelvic pain without bacteria present. Antibiotics don't help - it requires different treatments like bladder instillations or pelvic floor PT.",
    category: "Bladder Health"
  },
  {
    id: "myth-23",
    statement: "Natural or herbal remedies are always safer than prescribed hormones",
    isFact: false,
    explanation: "MYTH! 'Natural' doesn't mean safer. Many supplements lack regulation and evidence. Prescribed hormones are studied, regulated, and monitored for safety.",
    category: "Treatment"
  },
  {
    id: "fact-22",
    statement: "Perimenopause symptoms are often worse than post-menopause",
    isFact: true,
    explanation: "FACT! The hormonal fluctuations during perimenopause (not just low hormones) cause the most intense symptoms. Things often stabilize after menopause.",
    category: "Timeline"
  },
  {
    id: "myth-24",
    statement: "You're too young for menopause symptoms in your 40s",
    isFact: false,
    explanation: "MYTH! Perimenopause typically begins in your 40s. Some women start even earlier. If you're having symptoms, take them seriously regardless of age.",
    category: "Timeline"
  },
  {
    id: "fact-23",
    statement: "Kegel exercises alone may not fix all pelvic floor problems",
    isFact: true,
    explanation: "FACT! Some women have overly tight pelvic floors that need relaxation, not strengthening. A pelvic floor physical therapist can assess what you actually need.",
    category: "Bladder Health"
  },
  {
    id: "myth-25",
    statement: "Breast cancer screening is only needed if you have symptoms",
    isFact: false,
    explanation: "MYTH! The point of screening is to detect cancer BEFORE symptoms appear, when it's most treatable. Don't wait for symptoms to get screened.",
    category: "Breast Health"
  },
  {
    id: "fact-24",
    statement: "One alcoholic drink per day increases breast cancer risk by 7-10%",
    isFact: true,
    explanation: "FACT! Even moderate drinking increases risk. Each additional drink raises risk further. Consider limiting alcohol to special occasions.",
    category: "Breast Health"
  },
  {
    id: "myth-26",
    statement: "If your mother had an easy menopause, yours will be too",
    isFact: false,
    explanation: "MYTH! While genetics play a role in timing, symptom experience varies widely between family members. Your mother's journey doesn't predict yours.",
    category: "General"
  },
  {
    id: "fact-25",
    statement: "Estrogen affects over 400 functions in your body",
    isFact: true,
    explanation: "FACT! Estrogen impacts bones, brain, heart, skin, joints, mood, and more. This is why menopause symptoms are so widespread and varied.",
    category: "Hormones"
  },
  {
    id: "myth-27",
    statement: "Hormone therapy is only for severe symptoms",
    isFact: false,
    explanation: "MYTH! HRT can be appropriate even for moderate symptoms if they're affecting quality of life. You don't have to be at the end of your rope to deserve treatment.",
    category: "HRT"
  },
  {
    id: "myth-28",
    statement: "Caffeine causes breast lumps and cancer",
    isFact: false,
    explanation: "MYTH! No evidence links caffeine to breast cancer or lumps. Some women find breast pain improves with less caffeine, but it doesn't cause lumps or cancer.",
    category: "Breast Health"
  },
  {
    id: "fact-27",
    statement: "Stress management techniques can reduce hot flash frequency",
    isFact: true,
    explanation: "FACT! Mindfulness, meditation, and relaxation techniques can decrease hot flash frequency and severity. The mind-body connection is real.",
    category: "Lifestyle"
  },
  {
    id: "myth-29",
    statement: "All hormone replacement is the same",
    isFact: false,
    explanation: "MYTH! Routes (patch, pill, cream), types (estradiol vs conjugated estrogens), and formulations matter. Bio-identical, body-identical, and synthetic have different profiles.",
    category: "HRT"
  },
  {
    id: "fact-28",
    statement: "Pelvic floor physical therapy can treat many bladder and pelvic issues",
    isFact: true,
    explanation: "FACT! Specialized PT can help with urgency, frequency, pain, and incontinence. It's often more effective than medications for certain conditions.",
    category: "Bladder Health"
  }
];

const MythBuster = () => {
  const navigate = useNavigate();
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [swipedCards, setSwipedCards] = useState<{ id: string; correct: boolean }[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<"left" | "right" | null>(null);
  const [showResult, setShowResult] = useState<{ correct: boolean; explanation: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  // Load progress on mount
  useEffect(() => {
    const loadProgress = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUserId(user?.id || null);
        
        if (!user) {
          setIsLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('myth_buster_progress')
          .select('completed_cards')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading progress:', error);
        }

        if (data?.completed_cards) {
          const completed = data.completed_cards as { id: string; correct: boolean }[];
          setSwipedCards(completed);
          setCurrentCardIndex(completed.length);
        }
      } catch (error) {
        console.error('Error loading progress:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProgress();
  }, []);

  // Save progress whenever swipedCards changes
  useEffect(() => {
    const saveProgress = async () => {
      if (!userId || swipedCards.length === 0) return;

      try {
        const { error } = await supabase
          .from('myth_buster_progress')
          .upsert({
            user_id: userId,
            completed_cards: swipedCards
          }, {
            onConflict: 'user_id'
          });

        if (error) {
          console.error('Error saving progress:', error);
        }
      } catch (error) {
        console.error('Error saving progress:', error);
      }
    };

    saveProgress();
  }, [swipedCards, userId]);

  const currentCard = mythCards[currentCardIndex];
  const progress = (swipedCards.length / mythCards.length) * 100;
  const score = swipedCards.filter(c => c.correct).length;

  const handleSwipe = (direction: "left" | "right") => {
    if (isAnimating || !currentCard || showResult) return;

    const userSaysFact = direction === "right";
    const isCorrect = userSaysFact === currentCard.isFact;

    setSwipedCards(prev => [...prev, { id: currentCard.id, correct: isCorrect }]);
    setShowResult({ correct: isCorrect, explanation: currentCard.explanation });
  };

  const handleNextCard = () => {
    if (currentCardIndex < mythCards.length - 1) {
      setCurrentCardIndex(prev => prev + 1);
      setShowResult(null);
    }
  };

  const resetQuiz = async () => {
    setCurrentCardIndex(0);
    setSwipedCards([]);
    setSwipeDirection(null);
    setShowResult(null);
    
    // Clear progress from database if user is logged in
    if (userId) {
      try {
        await supabase
          .from('myth_buster_progress')
          .delete()
          .eq('user_id', userId);
      } catch (error) {
        console.error('Error clearing progress:', error);
      }
    }
    
    toast("Let's try again! Swipe away!");
  };

  const isComplete = swipedCards.length === mythCards.length;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your progress...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      {/* Background Image with Fade */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${mythBusterBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/90 to-background/95" />
      </div>

      {/* Content */}
      <div className="relative z-10">
      <div className="border-b border-border/40 bg-muted/20">
        <div className="container mx-auto px-6 py-16 max-w-5xl">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="mb-8 hover:bg-muted"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-5xl md:text-6xl font-serif font-bold mb-6 tracking-tight">Myth v Fact</h1>
          <p className="text-xl md:text-2xl text-muted-foreground font-serif leading-relaxed max-w-3xl">
            Test your knowledge of menopause myths and facts. Choose FACT for true statements, MYTH for false ones.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-16 max-w-3xl">
        <div className="mb-12 bg-muted/30 border border-border/40 p-6 rounded">
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              {swipedCards.length} / {mythCards.length} Questions
            </span>
            <span className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              Score: {score} / {swipedCards.length || 0}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {!isComplete ? (
          <div className="relative">
            <div className="absolute inset-x-0 -top-10 flex justify-center gap-12 text-sm font-medium uppercase tracking-wider">
              <div className="flex items-center gap-2 text-destructive">
                <ThumbsDown className="h-4 w-4" />
                Myth
              </div>
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <ThumbsUp className="h-4 w-4" />
                Fact
              </div>
            </div>

            <Card 
              className={`relative overflow-hidden transition-all duration-500 ${
                swipeDirection === "left" 
                  ? "translate-x-[-100%] rotate-[-15deg] opacity-0" 
                  : swipeDirection === "right"
                  ? "translate-x-[100%] rotate-[15deg] opacity-0"
                  : ""
              }`}
            >
              <div className={`p-10 md:p-14 min-h-[450px] flex flex-col justify-center items-center text-center transition-colors duration-300 ${
                showResult 
                  ? showResult.correct 
                    ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900" 
                    : "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900"
                  : "bg-gradient-to-br from-amber-50/80 via-orange-50/60 to-rose-50/80 dark:from-amber-950/20 dark:via-orange-950/20 dark:to-rose-950/20"
              }`}>
                <div className="mb-6">
                  <span className="inline-block px-5 py-2 bg-muted/50 border border-border/40 rounded-full text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {currentCard.category}
                  </span>
                </div>
                
                {showResult && (
                  <div className="mb-8 flex items-center gap-3">
                    {showResult.correct ? (
                      <CheckCircle2 className="h-10 w-10 text-green-700 dark:text-green-400" />
                    ) : (
                      <XCircle className="h-10 w-10 text-red-700 dark:text-red-400" />
                    )}
                    <span className="text-3xl font-serif font-bold">
                      {showResult.correct ? "Correct!" : "Not Quite!"}
                    </span>
                  </div>
                )}
                
                <h2 className="text-3xl md:text-4xl font-serif font-bold mb-8 text-foreground leading-tight max-w-2xl">
                  {currentCard.statement}
                </h2>
                
                {showResult ? (
                  <>
                    <p className="text-lg md:text-xl text-foreground/80 leading-relaxed max-w-2xl font-serif">
                      {showResult.explanation}
                    </p>
                    {currentCard.category === "HRT" && (
                      <Link
                        to="/article/hrt-explained-plain-english"
                        className="inline-block mt-6 text-primary underline underline-offset-4 font-serif italic hover:opacity-80"
                      >
                        Read the full plain-English guide to HRT →
                      </Link>
                    )}
                  </>
                ) : (
                  <p className="text-lg text-muted-foreground italic font-serif">
                    Choose your answer below...
                  </p>
                )}
              </div>
            </Card>

            {showResult ? (
              <div className="flex justify-center mt-10">
                <Button
                  size="lg"
                  onClick={handleNextCard}
                  className="min-w-[240px] text-base font-medium"
                >
                  Continue to Next Question
                </Button>
              </div>
            ) : (
              <div className="flex justify-center gap-6 mt-10">
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => handleSwipe("left")}
                  disabled={isAnimating}
                  className="flex items-center gap-3 border-2 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground min-w-[160px] text-base font-medium"
                >
                  <ThumbsDown className="h-5 w-5" />
                  Myth
                </Button>
                <Button
                  size="lg"
                  onClick={() => handleSwipe("right")}
                  disabled={isAnimating}
                  className="flex items-center gap-3 bg-green-700 hover:bg-green-800 dark:bg-green-600 dark:hover:bg-green-700 min-w-[160px] text-base font-medium"
                >
                  <ThumbsUp className="h-5 w-5" />
                  Fact
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            <Card className="bg-muted/20 border-border/40 p-10 md:p-14 text-center">
              <div className="flex justify-center mb-6">
                <div className="bg-primary text-primary-foreground rounded-full p-6">
                  <CheckCircle2 className="h-14 w-14" />
                </div>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">Quiz Complete!</h2>
              <p className="text-2xl text-muted-foreground font-serif mb-8">
                You scored {score} out of {mythCards.length}
              </p>
              <div className="text-xl md:text-2xl font-serif mb-10 text-foreground/80 italic">
                {score === mythCards.length && "Perfect score! You're a menopause myth-busting expert."}
                {score >= mythCards.length * 0.7 && score < mythCards.length && "Excellent work! You really know your facts."}
                {score >= mythCards.length * 0.5 && score < mythCards.length * 0.7 && "Good effort! Continue learning and exploring."}
                {score < mythCards.length * 0.5 && "Keep learning—knowledge is empowering."}
              </div>
              <Button onClick={resetQuiz} size="lg" className="gap-2 text-base">
                <RotateCcw className="h-5 w-5" />
                Retake the Quiz
              </Button>
            </Card>

            <div className="space-y-6">
              <h3 className="text-3xl font-serif font-bold border-b border-border/40 pb-4">Review Your Answers</h3>
              {swipedCards.map((swipedCard, index) => {
                const card = mythCards.find(c => c.id === swipedCard.id);
                if (!card) return null;
                
                return (
                  <Card key={card.id} className={`p-8 border-l-4 bg-muted/20 ${swipedCard.correct ? "border-green-600" : "border-red-600"}`}>
                    <div className="flex items-start gap-5">
                      {swipedCard.correct ? (
                        <CheckCircle2 className="h-7 w-7 text-green-700 dark:text-green-400 mt-1 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-7 w-7 text-red-700 dark:text-red-400 mt-1 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          <span className="font-serif font-bold text-xl">{card.statement}</span>
                          <span className={`text-xs px-3 py-1 rounded-full font-medium uppercase tracking-wider ${card.isFact ? "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300" : "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"}`}>
                            {card.isFact ? "Fact" : "Myth"}
                          </span>
                        </div>
                        <p className="text-muted-foreground leading-relaxed font-serif text-base">
                          {card.explanation}
                        </p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default MythBuster;